import { useEffect } from 'react';
import usePusher from '../../../helpers/hooks/usePusher';

type TitleUpdatedEvent = {
  type: 'title_updated';
  conversation_id: number;
  title: string;
};

/**
 * Listens for async `title_updated` events on the active conversation's Pusher
 * channel. The backend `TitleGenerationJob` fires this after generating the
 * title for the first turn of a new conversation, letting the sidebar update
 * without a manual refresh.
 *
 * Shares the `conversation_<id>` channel with the streaming pipeline — Pusher's
 * channel subscriptions are reference-counted, so binding a separate handler
 * here does not interfere with `usePusherStream`.
 */
export const useConversationTitleListener = (
  conversationId: number | null,
  onTitleUpdated: (id: number, title: string) => void
) => {
  const { pusherInstance } = usePusher();

  useEffect(() => {
    if (!pusherInstance || !conversationId) return;

    const channelName = `conversation_${conversationId}`;
    const channel = pusherInstance.subscribe(channelName);

    const handler = (data: unknown) => {
      const event = data as Partial<TitleUpdatedEvent> | null;
      if (!event || event.type !== 'title_updated') return;
      if (!event.conversation_id || !event.title) return;
      onTitleUpdated(event.conversation_id, event.title);
    };

    channel.bind('v2_stream', handler);

    return () => {
      channel.unbind('v2_stream', handler);
    };
  }, [pusherInstance, conversationId, onTitleUpdated]);
};
